import axios from 'axios';
import httpResource from 'modules/core/http-resource';

export interface ScrapJsonImportInput {
  tournamentId: number;
  file?: File | null;
  jsonUrl?: string;
}

export class ScrapJsonImportService {
  async importJson(input: ScrapJsonImportInput): Promise<{ success: boolean; message: string }> {
    try {
      const file = await this.resolveJsonFile(input);

      const formData = new FormData();
      formData.append('json', file);

      await httpResource.client.post('import-scrap-json', formData, {
        params: { tournamentId: input.tournamentId },
        timeout: 300_000,
      });

      return {
        success: true,
        message: 'Импорт JSON успешно завершён',
      };
    } catch (error) {
      return {
        success: false,
        message: this.formatError(error),
      };
    }
  }

  private async resolveJsonFile(input: ScrapJsonImportInput): Promise<File> {
    if (input.file) {
      return input.file;
    }

    if (!input.jsonUrl?.trim()) {
      throw new Error('Выберите JSON-файл или укажите URL');
    }

    const response = await fetch(input.jsonUrl.trim());

    if (!response.ok) {
      throw new Error(`Не удалось загрузить файл по URL (${response.status})`);
    }

    const blob = await response.blob();
    const fileName = this.extractFileName(input.jsonUrl, response);

    return new File([blob], fileName, { type: 'application/json' });
  }

  private extractFileName(url: string, response: Response): string {
    const contentDisposition = response.headers.get('content-disposition');
    const fileNameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);

    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    try {
      const pathname = new URL(url).pathname;
      const segment = pathname.split('/').filter(Boolean).pop();

      if (segment?.endsWith('.json')) {
        return segment;
      }
    } catch {
      // ignore invalid URL parsing
    }

    return 'import.json';
  }

  private formatError(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const responseMessage = error.response?.data;

      if (typeof responseMessage === 'string' && responseMessage.trim()) {
        return responseMessage;
      }

      if (responseMessage && typeof responseMessage === 'object' && 'message' in responseMessage) {
        return String(responseMessage.message);
      }

      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Неизвестная ошибка импорта';
  }
}
